import { useState } from 'react';
import { Link } from 'react-router';
import { Badge, Button, Dialog, Field, useToast } from '@/components/ui';
import { useAlertDecision } from '@/features/home-readings/api';
import {
  ALERT_STATUS_LABEL,
  ALERT_STATUS_TONE,
  KIND_LABEL,
  OUT_OF_RANGE_LABEL,
  SOURCE_LABEL,
  readingValue,
} from '@/features/home-readings/format';
import type { ReadingAlert } from '@/features/home-readings/types';
import { isApiError } from '@/lib/api';
import { when } from './rx-request-format';
import parity from '@/pages/ops/parity.module.css';
import styles from './HomeReadings.module.css';

const ERRORS: Record<string, string> = {
  VERSION_CONFLICT:
    'Someone else changed this alert. It has been reloaded: check it and try again.',
  READING_ALERT_RESOLVED:
    'This alert is already resolved. A new out-of-range reading opens a new alert.',
  READING_ALERT_NOT_FOUND: 'This alert is not in your branch any more.',
};

interface Failure {
  title: string;
  detail?: string;
  requestId?: string;
}

/**
 * One reading alert (RL-1141): the latest reading, who it was routed to and
 * the decision trail; acknowledge (optional note) or resolve (note required,
 * 2 to 1000 characters). Both send the alert's `version`.
 */
export function ReadingAlertDrawer({
  alert: a,
  canManage,
  onClose,
}: {
  alert: ReadingAlert;
  canManage: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const ack = useAlertDecision(a.id, 'acknowledge');
  const resolve = useAlertDecision(a.id, 'resolve');
  const [note, setNote] = useState('');
  const [noteError, setNoteError] = useState<string>();
  const [failure, setFailure] = useState<Failure | null>(null);
  const busy = ack.isPending || resolve.isPending;
  const active = a.status !== 'resolved';
  const r = a.latestReading;

  function fail(err: unknown) {
    if (!isApiError(err)) {
      setFailure({ title: 'Could not reach the server. Try again.' });
      return;
    }
    const field = err.fieldError('note');
    if (field) return setNoteError(field);
    const known = ERRORS[err.code];
    setFailure({
      title: known ?? err.message,
      detail: known && err.message !== known ? err.message : undefined,
      requestId: err.requestId,
    });
  }

  async function decide(action: 'acknowledge' | 'resolve') {
    setFailure(null);
    setNoteError(undefined);
    const text = note.trim();
    if (action === 'resolve' && text.length < 2)
      return setNoteError('Write what was done (at least 2 characters).');
    try {
      await (action === 'acknowledge' ? ack : resolve).mutateAsync({
        version: a.version,
        ...(text ? { note: text } : {}),
      });
      toast.show({
        tone: 'success',
        title:
          action === 'acknowledge' ? 'Alert acknowledged' : 'Alert resolved',
      });
      if (action === 'resolve') onClose();
      else setNote('');
    } catch (err) {
      fail(err);
    }
  }

  return (
    <Dialog
      open
      title={`${KIND_LABEL[a.kind]} alert · ${a.patient.fullName}`}
      onClose={onClose}
      dismissible={!busy}
      footer={
        canManage && active ? (
          <>
            <Button onClick={onClose} disabled={busy}>
              Close
            </Button>
            {a.status === 'open' && (
              <Button
                loading={ack.isPending}
                disabled={busy}
                onClick={() => void decide('acknowledge')}
              >
                Acknowledge
              </Button>
            )}
            <Button
              variant="primary"
              loading={resolve.isPending}
              disabled={busy}
              onClick={() => void decide('resolve')}
            >
              Resolve
            </Button>
          </>
        ) : (
          <Button onClick={onClose}>Close</Button>
        )
      }
    >
      <div className={styles.drawer}>
        <dl className={parity.kv}>
          <dt>Status</dt>
          <dd>
            <Badge tone={ALERT_STATUS_TONE[a.status]} dot>
              {ALERT_STATUS_LABEL[a.status]}
            </Badge>
          </dd>
          <dt>Latest reading</dt>
          <dd>
            {r ? (
              <>
                {readingValue(r)}
                {r.outOfRange && ` · ${OUT_OF_RANGE_LABEL[r.outOfRange]}`}
                <span className={parity.sub}>
                  Measured {when(r.measuredAt)} · {SOURCE_LABEL[r.source]}
                  {r.deviceName ? ` (${r.deviceName})` : ''}
                </span>
              </>
            ) : (
              '—'
            )}
          </dd>
          <dt>Out-of-range readings</dt>
          <dd>{a.readingCount}</dd>
          <dt>Raised</dt>
          <dd>{when(a.createdAt)}</dd>
          <dt>Branch</dt>
          <dd>{a.branch.name}</dd>
          <dt>Routed to</dt>
          <dd>{a.doctor?.fullName ?? 'Branch care team'}</dd>
          {a.acknowledgedAt && (
            <>
              <dt>Acknowledged</dt>
              <dd>
                {when(a.acknowledgedAt)}
                {a.acknowledgedBy?.fullName
                  ? ` by ${a.acknowledgedBy.fullName}`
                  : ''}
              </dd>
            </>
          )}
          {a.resolvedAt && (
            <>
              <dt>Resolved</dt>
              <dd>
                {when(a.resolvedAt)}
                {a.resolvedBy?.fullName ? ` by ${a.resolvedBy.fullName}` : ''}
              </dd>
            </>
          )}
          {a.note && (
            <>
              <dt>Note</dt>
              <dd className={styles.lines}>{a.note}</dd>
            </>
          )}
        </dl>
        <p>
          <Link to={`/patients/${a.patient.id}`}>
            Open the patient record (home readings and thresholds)
          </Link>
        </p>
        {failure && (
          <div className={styles.failure} role="alert">
            <strong>{failure.title}</strong>
            {failure.detail && <span>{failure.detail}</span>}
            {failure.requestId && (
              <span className={styles.muted}>
                Reference: {failure.requestId}
              </span>
            )}
          </div>
        )}
        {canManage && active && (
          <Field
            label="Note"
            hint="Optional to acknowledge; required to resolve: what was done."
            error={noteError}
          >
            <textarea
              className={parity.textarea}
              rows={3}
              maxLength={1000}
              value={note}
              disabled={busy}
              onChange={(e) => setNote(e.target.value)}
            />
          </Field>
        )}
        {!active && (
          <p className={styles.muted}>
            A new out-of-range reading opens a new alert.
          </p>
        )}
      </div>
    </Dialog>
  );
}
